import { useEffect } from 'react';

/**
 * Componente para adicionar dados estruturados (JSON-LD) para SEO
 * Isso ajuda o Google a entender o conteúdo e exibir sitelinks
 */
export const StructuredData = ({ data }) => {
  useEffect(() => {
    // Adicionar script JSON-LD ao head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    script.id = 'structured-data';
    
    // Remover script anterior se existir
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [data]);

  return null;
};

const BASE_URL = 'https://arukdog.com.br';

/**
 * Dados estruturados para a página inicial (Organization/WebSite)
 */
export const HomeStructuredData = () => {
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${BASE_URL}/#organization`,
        "name": "Aruk Dog",
        "url": BASE_URL,
        "logo": {
          "@type": "ImageObject",
          "url": `${BASE_URL}/Preto.png`
        },
        "description": "Aruk Dog - Banho e Tosa com Amor e Cuidado para seu Pet",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "BR"
        },
        "sameAs": []
      },
      {
        "@type": "WebSite",
        "@id": `${BASE_URL}/#website`,
        "url": BASE_URL,
        "name": "Aruk Dog",
        "description": "Aruk Dog - Banho e Tosa com Amor e Cuidado para seu Pet",
        "publisher": {
          "@id": `${BASE_URL}/#organization`
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${BASE_URL}/blog?search={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "Service",
        "name": "Banho e Tosa para Pets",
        "description": "Serviços de banho, tosa e cuidados para pets com profissionalismo e carinho",
        "provider": {
          "@id": `${BASE_URL}/#organization`
        },
        "tipoServico": "Banho e Tosa",
        "areaServed": {
          "@type": "Country",
          "name": "Brasil"
        }
      }
    ]
  };

  return <StructuredData data={data} />;
};

/**
 * Dados estruturados para uma postagem do blog (Article/BlogPosting)
 * Vincula o post ao Blog e à Organization Aruk Dog para aparecer como tópico do site na pesquisa.
 */
export const BlogPostStructuredData = ({ post }) => {
  if (!post) return null;

  const postUrl = `${BASE_URL}/blog/${post.id}`;
  const imageUrl = post.urlImagem || post.imageUrl || `${BASE_URL}/Preto.png`;
  
  const formatDate = (date) => {
    if (!date) return new Date().toISOString();
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toISOString();
  };

  const data = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.titulo || post.title,
    "description": post.resumo || post.excerpt || post.titulo || post.title,
    "image": imageUrl,
    "datePublished": formatDate(post.criadoEm),
    "dateModified": formatDate(post.atualizadoEm || post.criadoEm),
    "author": {
      "@type": "Organization",
      "@id": `${BASE_URL}/#organization`,
      "name": "Aruk Dog",
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "@id": `${BASE_URL}/#organization`,
      "name": "Aruk Dog",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/Preto.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": postUrl
    },
    "isPartOf": {
      "@type": "Blog",
      "@id": `${BASE_URL}/blog#blog`,
      "name": "Blog Aruk Dog",
      "publisher": { "@id": `${BASE_URL}/#organization` }
    },
    "url": postUrl,
    "articleBody": (post.conteudo || post.content) ? (post.conteudo || post.content).replace(/<[^>]*>/g, '').substring(0, 500) : (post.resumo || post.excerpt) || ''
  };

  return <StructuredData data={data} />;
};

/**
 * Dados estruturados para a página do blog (Blog)
 * Identifica o blog como parte do site Aruk Dog para a pesquisa.
 */
export const BlogStructuredData = ({ posts = [] }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${BASE_URL}/blog#blog`,
    "name": "Blog Aruk Dog",
    "description": "Dicas, cuidados e novidades sobre o mundo pet. Conteúdo do Aruk Dog sobre banho, tosa e cuidados para seu pet.",
    "url": `${BASE_URL}/blog`,
    "publisher": {
      "@type": "Organization",
      "@id": `${BASE_URL}/#organization`,
      "name": "Aruk Dog",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/Preto.png`
      }
    },
    "blogPost": posts.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.titulo || post.title,
      "description": post.resumo || post.excerpt || post.titulo || post.title,
      "url": `${BASE_URL}/blog/${post.id}`,
      "datePublished": post.criadoEm ? (post.criadoEm.toDate ? post.criadoEm.toDate().toISOString() : new Date(post.criadoEm).toISOString()) : new Date().toISOString(),
      "image": post.urlImagem || post.imageUrl || `${BASE_URL}/Preto.png`,
      "author": { "@id": `${BASE_URL}/#organization` }
    }))
  };

  return <StructuredData data={data} />;
};
